import dayjs from 'dayjs';

export default function($apiInstance, $store) {
    return {
        // 보고서 계산 (재계산)
        calculateReport: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            try {
                const requestBody = {
                    eventId: params.eventId,
                    periodType: params.periodType || 'custom',
                    startDate: params.startDate,
                    endDate: params.endDate
                };

                const response = await $apiInstance.post('/events-v2/reports/calculate', requestBody);

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error calculating report:', error);
                if (fail) fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 보고서 조회
        getReport: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            try {
                if (!params.eventId) {
                    throw new Error('eventId is required');
                }

                const queryParams = {
                    startDate: params.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
                    endDate: params.endDate || dayjs().format('YYYY-MM-DD')
                };

                // periodType이 있으면 추가
                if (params.periodType) {
                    queryParams.periodType = params.periodType;
                }

                // eventId를 URL 파라미터로 전달
                const response = await $apiInstance.get(`/events-v2/reports/${params.eventId}`, {
                    params: queryParams
                });

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error fetching report:', error);
                if (fail) fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 그룹별 상세 데이터 조회
        getGroupDetails: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            try {
                if (!params.reportId) {
                    throw new Error('reportId is required');
                }
                if (!params.group) {
                    throw new Error('group is required');
                }

                const queryParams = {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    sortBy: params.sortBy || 'surgeryDate',
                    sortOrder: params.sortOrder || 'desc'
                };

                const response = await $apiInstance.get(
                    `/events-v2/reports/${params.reportId}/groups/${params.group}/details`,
                    { params: queryParams }
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error fetching group details:', error);
                if (fail) fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 보정치 업데이트
        updateAdjustments: async (params, success, fail) => {
            try {
                if (!params.reportId) {
                    throw new Error('reportId is required');
                }
                if (!params.adjustments) {
                    throw new Error('adjustments is required');
                }

                const response = await $apiInstance.patch(
                    `/events-v2/reports/${params.reportId}/adjustments`,
                    { adjustments: params.adjustments }
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error updating adjustments:', error);
                if (fail) fail(error);
            }
        },

        // 보정치 조회
        getAdjustments: async (params, success, fail) => {
            try {
                if (!params.reportId) {
                    throw new Error('reportId is required');
                }

                const response = await $apiInstance.get(
                    `/events-v2/reports/${params.reportId}/adjustments`
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error fetching adjustments:', error);
                if (fail) fail(error);
            }
        },

        // 보정치 삭제
        deleteAdjustment: async (params, success, fail) => {
            try {
                if (!params.reportId) {
                    throw new Error('reportId is required');
                }
                if (!params.fieldPath) {
                    throw new Error('fieldPath is required');
                }

                const response = await $apiInstance.delete(
                    `/events-v2/reports/${params.reportId}/adjustments/${params.fieldPath}`
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error deleting adjustment:', error);
                if (fail) fail(error);
            }
        },

        // 이벤트별 병원 예약 통계 조회
        getEventReservationStatistics: async (params, success, fail) => {
            try {
                if (!params.eventId) {
                    throw new Error('eventId is required');
                }

                const queryParams = {};
                if (params.startDate) queryParams.startDate = params.startDate;
                if (params.endDate) queryParams.endDate = params.endDate;
                if (params.medicalDept) queryParams.medicalDept = params.medicalDept;

                const response = await $apiInstance.get(
                    `/operation-hospital-reservations/events/${params.eventId}/statistics`,
                    { params: queryParams }
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error fetching event reservation statistics:', error);
                if (fail) fail(error);
            }
        },

        // 병원 예약 통계를 EventReport에 저장
        updateHospitalReservationStats: async (params, success, fail) => {
            try {
                if (!params.reportId) {
                    throw new Error('reportId is required');
                }
                if (!params.hospitalReservationStats) {
                    throw new Error('hospitalReservationStats is required');
                }

                const response = await $apiInstance.patch(
                    `/events-v2/reports/${params.reportId}/hospital-reservation-stats`,
                    params.hospitalReservationStats
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error updating hospital reservation stats:', error);
                if (fail) fail(error);
            }
        },

        // 이벤트별 리뷰 상세 통계 조회
        getEventReviewStatistics: async (params, success, fail) => {
            try {
                if (!params.eventId) {
                    throw new Error('eventId is required');
                }

                const queryParams = {};
                if (params.startDate) queryParams.startDate = params.startDate;
                if (params.endDate) queryParams.endDate = params.endDate;
                if (params.isTestCompany) queryParams.isTestCompany = params.isTestCompany;

                const response = await $apiInstance.get(
                    `/reviews/events/${params.eventId}/statistics`,
                    { params: queryParams }
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error fetching event review statistics:', error);
                if (fail) fail(error);
            }
        },

        // 리뷰 통계를 EventReport에 저장
        updateReviewStats: async (params, success, fail) => {
            try {
                if (!params.reportId) {
                    throw new Error('reportId is required');
                }
                if (!params.reviewStats) {
                    throw new Error('reviewStats is required');
                }

                const response = await $apiInstance.patch(
                    `/events-v2/reports/${params.reportId}/review-stats`,
                    params.reviewStats
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error updating review stats:', error);
                if (fail) fail(error);
            }
        },

        // 이벤트별 쿠폰 통계 계산 (eventattendees.couponInfo 기반)
        getEventCouponStatistics: async (params, success, fail) => {
            try {
                if (!params.eventId) {
                    throw new Error('eventId is required');
                }

                const response = await $apiInstance.get(
                    `/events-v2/reports/coupon-statistics/${params.eventId}`
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error fetching event coupon statistics:', error);
                if (fail) fail(error);
            }
        },

        // 쿠폰 통계를 EventReport에 저장
        updateCouponStats: async (params, success, fail) => {
            try {
                if (!params.reportId) {
                    throw new Error('reportId is required');
                }
                if (!params.couponStats) {
                    throw new Error('couponStats is required');
                }

                const response = await $apiInstance.patch(
                    `/events-v2/reports/${params.reportId}/coupon-stats`,
                    params.couponStats
                );

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response.data);
                }
            } catch (error) {
                console.error('Error updating coupon stats:', error);
                if (fail) fail(error);
            }
        }
    };
}
